/**
 * Sprint API Client
 *
 * Communicates with the orchestration service sprint endpoints.
 * Simplified lifecycle: planning -> building -> completed|failed
 * No lock step — build transitions directly from planning.
 */

const API_BASE = '/api/orchestration';

export interface BuildConfig {
  maxConcurrentAgents: number;
  timeoutMinutes: number;
  trustLevel?: number;
  retryPolicy?: { maxRetries: number; backoffMs: number };
  executionStrategy?: 'parallel' | 'sequential';
  pluginOverrides?: Record<string, boolean>;
}

export interface Sprint {
  id: string;
  projectId: string;
  name: string;
  status: 'planning' | 'building' | 'completed' | 'failed';
  epicIds: string[];
  readinessThreshold: number;
  createdAt: string;
  startedAt?: string;
  completedAt?: string;
  failedAt?: string;
  buildConfig?: BuildConfig;
  batchId?: string;
  queueId?: string;
  execution?: {
    epicCount: number;
    storyCount: number;
    repositoryUrl?: string;
    branch: string;
  };
}

export interface SprintEpic {
  epicId: string;
  sprintId: string;
  readinessScore: number;
  readinessStatus: 'green' | 'amber' | 'red';
  order: number;
  status: 'pending' | 'building' | 'completed' | 'failed' | 'skipped';
  dependencies: string[];
  storyOrder?: string[] | null;
}

// Integration Testing Types (kept for integration scan display)
export type IntegrationType =
  | 'rest_api' | 'graphql' | 'database' | 'message_queue'
  | 'webhook' | 'oauth' | 'file_storage' | 'grpc' | 'websocket' | 'smtp';

export type AuthMethod =
  | 'none' | 'api_key' | 'bearer_token' | 'oauth2'
  | 'basic_auth' | 'mtls' | 'hmac_signature';

export interface DiscoveredIntegration {
  id: string;
  serviceName: string;
  type: IntegrationType;
  endpoint?: string;
  authMethod: AuthMethod;
  description: string;
  specReference: string;
  confidence: number;
}

export interface IntegrationTestStrategy {
  integrationId: string;
  contractTests: string[];
  connectionTests: string[];
  authTests: string[];
  dataFlowTests: string[];
  estimatedDurationSeconds: number;
  priority: 'critical' | 'standard' | 'optional';
}

export interface IntegrationScanResult {
  epicId: string;
  integrations: DiscoveredIntegration[];
  testStrategies: IntegrationTestStrategy[];
  scanConfidence: number;
  scannedAt: string;
  specDocId?: string;
}

export interface PreflightCheck {
  name: string;
  category: 'credentials' | 'services' | 'repository' | 'configuration';
  status: 'pass' | 'fail' | 'warn';
  message: string;
}

export interface PreflightReport {
  sprintId: string;
  projectId: string;
  timestamp: string;
  overallStatus: 'pass' | 'fail';
  checks: PreflightCheck[];
  durationMs: number;
}

export interface FileScope {
  predicted_files: string[];
  predicted_dirs: string[];
  confidence: number;
  analyzed_at?: string;
}

export interface ConflictReport {
  conflicts: Array<{
    file: string;
    epicIds: string[];
    severity: 'high' | 'medium' | 'low';
  }>;
  epicOverlaps: Array<{
    epicA: string;
    epicB: string;
    sharedFiles: string[];
    risk: string;
  }>;
  recommendations: Array<{
    type: string;
    message: string;
    epicIds: string[];
  }>;
  overallRisk: 'safe' | 'caution' | 'danger';
}

/** Default build config for sprint builds */
export const DEFAULT_BUILD_CONFIG: BuildConfig = {
  maxConcurrentAgents: 2,
  timeoutMinutes: 60,
};

/* ── Shared Issue Execution Types & Helpers ────────────────── */

export interface IssueExecution {
  issueId: string;
  issueTitle: string | null;
  epicId: string | null;
  spriteId: string | null;
  branch: string | null;
  status: 'pending' | 'running' | 'success' | 'failure';
  startedAt: string | null;
  completedAt: string | null;
  durationMs: number | null;
  filesChanged: number;
  commits: Array<{ sha: string; message: string }>;
  errorMessage: string | null;
}

export async function fetchSprintIssueExecutions(workflowId: string): Promise<IssueExecution[]> {
  const res = await fetch(`${API_BASE}/activity/workflows/${encodeURIComponent(workflowId)}/issues`);
  if (!res.ok) return [];
  const data = await res.json();
  return data.issues || [];
}

export function execStatusToWaterfall(status: IssueExecution['status']): 'pending' | 'building' | 'completed' | 'failed' {
  switch (status) {
    case 'success': return 'completed';
    case 'failure': return 'failed';
    case 'running': return 'building';
    default: return 'pending';
  }
}

/* ── Sprint API Client ─────────────────────────────────────── */

export const sprintApi = {
  /**
   * Create a new sprint for a project with selected epic IDs.
   */
  async create(
    projectId: string,
    name: string,
    epicIds: string[],
    readinessThreshold?: number,
    epicDependencies?: Record<string, string[]>,
  ): Promise<Sprint> {
    const res = await fetch(`${API_BASE}/sprints`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ projectId, name, epicIds, readinessThreshold, epicDependencies }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({ details: res.statusText }));
      throw new Error(err.details || `Failed to create sprint: ${res.statusText}`);
    }
    return res.json();
  },

  /**
   * Delete a planning sprint.
   */
  async delete(sprintId: string): Promise<void> {
    const res = await fetch(`${API_BASE}/sprints/${sprintId}`, { method: 'DELETE' });
    if (!res.ok) {
      const err = await res.json().catch(() => ({ details: res.statusText }));
      throw new Error(err.details || `Failed to delete sprint: ${res.statusText}`);
    }
  },

  /**
   * Get a sprint by its ID.
   */
  async get(sprintId: string): Promise<Sprint> {
    const res = await fetch(`${API_BASE}/sprints/${sprintId}`);
    if (!res.ok) throw new Error(`Failed to get sprint: ${res.statusText}`);
    return res.json();
  },

  /**
   * List all sprints for a project, optionally filtered by status.
   */
  async list(
    projectId: string,
    status?: Sprint['status'],
  ): Promise<{ sprints: Sprint[]; count: number }> {
    let url = `${API_BASE}/sprints?projectId=${encodeURIComponent(projectId)}`;
    if (status) url += `&status=${status}`;
    const res = await fetch(url);
    if (!res.ok) throw new Error(`Failed to list sprints: ${res.statusText}`);
    return res.json();
  },

  /**
   * Start building a sprint (planning -> building).
   * Replaces the old lock-then-build two-step flow.
   * Quick credential check, then starts CodeExecutionWorkflow via Temporal.
   */
  async startBuild(
    sprintId: string,
    buildConfig: BuildConfig = DEFAULT_BUILD_CONFIG,
  ): Promise<Sprint> {
    const res = await fetch(`${API_BASE}/sprints/${sprintId}/build`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(buildConfig),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({ details: res.statusText }));
      throw new Error(err.details || `Failed to start build: ${res.statusText}`);
    }
    return res.json();
  },

  /**
   * Mark a sprint as completed.
   */
  async complete(sprintId: string): Promise<Sprint> {
    const res = await fetch(`${API_BASE}/sprints/${sprintId}/complete`, {
      method: 'POST',
    });
    if (!res.ok) throw new Error(`Failed to complete sprint: ${res.statusText}`);
    return res.json();
  },

  /**
   * Mark a sprint as failed.
   */
  async fail(sprintId: string, reason: string): Promise<Sprint> {
    const res = await fetch(`${API_BASE}/sprints/${sprintId}/fail`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ reason }),
    });
    if (!res.ok) throw new Error(`Failed to fail sprint: ${res.statusText}`);
    return res.json();
  },

  /**
   * Get all epics associated with a sprint.
   */
  async getEpics(sprintId: string): Promise<{ sprintId: string; epics: SprintEpic[]; count: number }> {
    const res = await fetch(`${API_BASE}/sprints/${sprintId}/epics`);
    if (!res.ok) throw new Error(`Failed to get sprint epics: ${res.statusText}`);
    return res.json();
  },

  /**
   * Replace the set of epics in a planning sprint.
   */
  async updateEpics(sprintId: string, epicIds: string[]): Promise<Sprint> {
    const res = await fetch(`${API_BASE}/sprints/${sprintId}/epics`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ epicIds }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({ details: res.statusText }));
      throw new Error(err.details || `Failed to update sprint epics: ${res.statusText}`);
    }
    return res.json();
  },

  /**
   * Get integration scan results for all epics in a sprint.
   */
  async getIntegrationScan(
    sprintId: string,
  ): Promise<{ sprintId: string; epicCount: number; scans: IntegrationScanResult[]; totalIntegrations: number }> {
    const res = await fetch(`${API_BASE}/sprints/${sprintId}/integration-scan`);
    if (!res.ok) throw new Error(`Failed to get integration scan: ${res.statusText}`);
    return res.json();
  },

  /**
   * Update the status of an epic within a sprint.
   */
  async updateEpicStatus(
    sprintId: string,
    epicId: string,
    status: SprintEpic['status'],
  ): Promise<{ success: boolean; sprintId: string; epicId: string; status: string }> {
    const res = await fetch(`${API_BASE}/sprints/${sprintId}/epics/${epicId}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ status }),
    });
    if (!res.ok) throw new Error(`Failed to update epic status: ${res.statusText}`);
    return res.json();
  },

  /**
   * Analyze file scope for all stories in a sprint.
   */
  async analyzeScope(
    sprintId: string,
    fileTree?: string[],
  ): Promise<{
    sprintId: string;
    scopeResults: Array<{ storyId: string; fileScope: FileScope }>;
    storiesAnalyzed: number;
  } & ConflictReport> {
    const res = await fetch(`${API_BASE}/sprints/${sprintId}/analyze-scope`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ fileTree }),
    });
    if (!res.ok) throw new Error(`Failed to analyze scope: ${res.statusText}`);
    return res.json();
  },

  /**
   * Update the story (issue) ordering for an epic within a sprint.
   */
  async updateStoryOrder(sprintId: string, epicId: string, issueIds: string[]): Promise<void> {
    const res = await fetch(`${API_BASE}/sprints/${sprintId}/epics/${epicId}/story-order`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ issueIds }),
    });
    if (!res.ok) throw new Error('Failed to update story order');
  },

  /**
   * Get conflict analysis for a sprint.
   */
  async getConflictAnalysis(
    sprintId: string,
  ): Promise<{ sprintId: string } & ConflictReport> {
    const res = await fetch(`${API_BASE}/sprints/${sprintId}/conflict-analysis`);
    if (!res.ok) throw new Error(`Failed to get conflict analysis: ${res.statusText}`);
    return res.json();
  },
};

/* ── Sprint Runs (multi-attempt tracking) ─────────────────── */

export interface SprintRun {
  id: string;
  sprintId: string;
  batchId: string | null;
  status: string;
  attemptNumber: number;
  startedAt: string | null;
  completedAt: string | null;
  failedAt: string | null;
  storiesCompleted: number;
  storiesFailed: number;
  storiesTotal: number;
  failureReason: string | null;
  planSnapshot: unknown | null;
  createdAt: string;
}

export interface SprintRunsResponse {
  runs: SprintRun[];
  count: number;
}

export async function fetchSprintRuns(sprintId: string): Promise<SprintRunsResponse> {
  const res = await fetch(`${API_BASE}/sprints/${encodeURIComponent(sprintId)}/runs`);
  if (!res.ok) return { runs: [], count: 0 };
  return res.json();
}

/* ── Sprint Plan (for telemetry Plan tab) ─────────────────── */

export interface SprintPlanStory {
  id: string;
  title: string;
  planStatus: string;
  execStatus: string | null;
  startedAt: string | null;
  completedAt: string | null;
  durationMs: number | null;
  commits: Array<{ sha: string; message: string }>;
  filesChanged: number;
  errorMessage: string | null;
  spriteId: string | null;
}

export interface SprintPlanEpic {
  epicId: string;
  title: string;
  status: string;
  order: number;
  readinessScore: number;
  readinessStatus: string;
  stories: SprintPlanStory[];
  storyCounts: {
    total: number;
    completed: number;
    running: number;
    failed: number;
    pending: number;
  };
}

export interface SprintPlan {
  sprintId: string;
  sprintName: string;
  sprintStatus: string;
  startedAt: string | null;
  completedAt: string | null;
  failedAt: string | null;
  batchId: string | null;
  epics: SprintPlanEpic[];
  totalStories: number;
  timestamp: string;
}

export async function fetchSprintPlan(sprintId: string): Promise<SprintPlan> {
  const res = await fetch(`${API_BASE}/activity/sprints/${encodeURIComponent(sprintId)}/plan`);
  if (!res.ok) throw new Error(`Failed to fetch sprint plan: ${res.statusText}`);
  return res.json();
}

